import * as React from "react";
import NavBar from "../components/NavBar";
import { RouteChildrenProps } from "react-router";
import { Modal, Pagination, Button } from "react-bootstrap";
import "./DataListPage.css";
import { firestore, storage } from "firebase";
import * as fs from "../firestore";
import {createAnalysisImageKey, createSamplingImageKey, tryGetDownloadUrl} from "../storage";
import SamplingResultsTable, { SamplingResult } from "../components/SamplingResultsTable";
import AnalysisResultsTable, { AnalysisResult } from "../components/AnalysisResultsTable";
import AppUser from "../AppUser";
import firebase from "../firebase";
import { DownloadStatus } from "../DownloadStatus";

const storageRef = storage().ref();
const usersRef = firestore().collection(fs.Collection.Users);
const samplingResultsRef = firestore().collection(fs.Collection.SamplingResults);
const analysisResultsRef = firestore().collection(fs.Collection.AnalysisResults);
const PAGINATION_LIMIT = 20;

function toBlob(base64: string, mimeType: string): Blob|null {
    const bin = atob(base64.replace(/^.*,/, ''));
    const buffer = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) {
        buffer[i] = bin.charCodeAt(i);
    }
    try {
        const blob = new Blob([buffer.buffer], { type: mimeType });
        return blob;
    } catch (e) {
        return null;
    }
}

interface Props extends RouteChildrenProps {
    user: AppUser
}

interface State {
    showAnalysisModal: boolean,
    showDownloadModal: boolean;
    downloadStatus: DownloadStatus;
    downloadedSamplingCount: number;
    downloadedAnalysisCount: number;
    selectedSamplingResultId: string,
    samplingResults: SamplingResult[],
    analysisResults: AnalysisResult[]
}

export default abstract class DataListPage extends React.Component<Props, State> {
    private onRequestShowAnalysisResultsBound: (samplingResultId: string) => void;
    private onClickNextPageBound: () => void;
    private onClickPrevPageBound: () => void;
    private onClickDownloadButtonBound: () => void;
    private onClickStartDownloadBound: () => void;
    private onClickCloseModalBound: () => void;
    private downloadTrigger: HTMLAnchorElement|null;
    private onClickDownloadOKBound: () => void;

    constructor(props: Props) {
        super(props);
        this.onRequestShowAnalysisResultsBound = this.onRequestShowAnalysisResults.bind(this);
        this.onClickNextPageBound = this.onClickNextPage.bind(this);
        this.onClickPrevPageBound = this.onClickPrevPage.bind(this);
        this.onClickDownloadButtonBound = this.onClickDownloadButton.bind(this);
        this.onClickStartDownloadBound = this.onClickStartDownload.bind(this);
        this.downloadTrigger = null;
        this.onClickCloseModalBound = this.onClickCloseModal.bind(this);
        this.onClickDownloadOKBound = this.onClickDownloadOK.bind(this);

        this.state = {
            showAnalysisModal: false,
            showDownloadModal: false,
            downloadStatus: DownloadStatus.None,
            downloadedSamplingCount: 0,
            downloadedAnalysisCount: 0,
            selectedSamplingResultId: "",
            samplingResults: [],
            analysisResults: []
        };
    }

    abstract getPageTitle(): string;
    abstract getExcelGenerationFunction(): string;
    abstract getShowDeleteFlag(): boolean;
    abstract makeFetchSamplingResultsQuery(limit: number, startAfter: SamplingResult|null, reverseOrder: boolean): firestore.Query;
    abstract makeFetchAnalysisResultsQuery(samplintResultId: string): firestore.Query;

    async componentDidMount() {
        const samplingResults = await this.fetchSamplingResults();
        this.setState({
            samplingResults
        });
    }

    async onRequestShowAnalysisResults(samplingResultId: string) {
        const analysisResults = await this.fetchAnalysisResults(samplingResultId);
        this.setState({
            showAnalysisModal: true,
            selectedSamplingResultId: samplingResultId,
            analysisResults
        });
    }

    async onClickNextPage() {
        if (!this.state.samplingResults.length) return;
        const samplingResults = await this.fetchSamplingResults(this.state.samplingResults.slice(-1)[0]);
        if (!samplingResults.length) return;
        this.setState({samplingResults});
    }

    async onClickPrevPage() {
        if (!this.state.samplingResults.length) return;
        const samplingResults = await this.fetchSamplingResults(this.state.samplingResults[0], true);
        if (!samplingResults.length) return;
        // NOTE: 前のページのデータ取得時に通常とは逆の並び順で取得しているため、reverse()で元に戻している
        this.setState({samplingResults: samplingResults.reverse()});
    }

    onClickCloseModal() {
        this.setState({
            showAnalysisModal: false
        });
    }

    private async createUserIdNameMap(userIds: string[]): Promise<{[userId: string]: string}> {
        const result: {[userId: string]: string} = {};
        const userNames = await Promise.all(userIds.map(async id => {
            const user = (await firestore().collection(fs.Collection.Users).doc(id).get()).data() as fs.User;
            return user.name;
        }));
        userIds.forEach((id, index) => {
            result[id] = userNames[index];
        });
        return result;
    }

    private async fetchSamplingResults(startAfter: SamplingResult|null = null, reverseOrder: boolean = false): Promise<SamplingResult[]> {
        const query = this.makeFetchSamplingResultsQuery(PAGINATION_LIMIT, startAfter, reverseOrder);
        const userIdSet = new Set<string>();
        const rawSamplingResults = (await query.get()).docs.map(doc => {
            const data = doc.data() as fs.SamplingResult;
            userIdSet.add(data.userRef.id);
            return {id: doc.id, data};
        });
        const userIdNameMap = await this.createUserIdNameMap(Array.from(userIdSet));
        return rawSamplingResults.map(rawSamplingResult => {
            return {
                id: rawSamplingResult.id,
                userName: userIdNameMap[rawSamplingResult.data.userRef.id],
                ...rawSamplingResult.data
            };
        });
    }

    private async fetchAnalysisResults(samplingResultId: string): Promise<AnalysisResult[]> {
        const query = this.makeFetchAnalysisResultsQuery(samplingResultId);
        const userIdSet = new Set<string>();
        const rawAnalysisResults = (await query.get()).docs.map(doc => {
            const data = doc.data() as fs.AnalysisResult;
            userIdSet.add(data.userRef.id);
            return {id: doc.id, data};
        });
        const userIdNameMap = await this.createUserIdNameMap(Array.from(userIdSet));
        return rawAnalysisResults.map(rawAnalysisResult => {
            return {
                id: rawAnalysisResult.id,
                userName: userIdNameMap[rawAnalysisResult.data.userRef.id],
                ...rawAnalysisResult.data
            };
        });
    }

    onClickDownloadOK() {
        this.setState({
            showDownloadModal: false,
            downloadStatus: DownloadStatus.None
        });
    }

    private onClickDownloadButton() {
        this.setState({showDownloadModal: true});
    }

    private async onClickStartDownload() {
        try {
            this.setState({downloadStatus: DownloadStatus.Downloading});
            const d = await firebase.functions().httpsCallable(this.getExcelGenerationFunction())();
            if (!this.downloadTrigger) {
                this.setState({showDownloadModal: false});
                alert("ダウンロードに失敗しました, Error: 1000");
                return;
            }
            const blob = toBlob(d.data.excelData, d.data.mimeType);
            if (!blob) {
                this.setState({showDownloadModal: false});
                alert("ダウンロードに失敗しました, Error: 1001");
                return;
            }
            this.downloadTrigger.href = window.URL.createObjectURL(blob);
            this.downloadTrigger.download = d.data.filename;
            this.downloadTrigger.click();
            this.setState({
                downloadStatus: DownloadStatus.Completed,
                downloadedSamplingCount: d.data.samplingCount,
                downloadedAnalysisCount: d.data.analysisCount
            });
        } catch (e) {
            console.error(e);
            this.setState({showDownloadModal: false});
            alert("ダウンロードに失敗しました, Error: 1002");
        }
    }

    private renderDownloadModal(): JSX.Element {
        const renderNoneBody = () => {
            return (
                <Modal.Body>
                    <p><a href="#" onClick={this.onClickStartDownloadBound}>{"ダウンロード開始"}</a></p>
                </Modal.Body>
            );
        };
        const renderDownloadingBody = () => {
            return (
                <Modal.Body>
                    <p>{"ダウンロード中です"}</p>
                </Modal.Body>
            );
        };
        const renderCompletedBody = () => {
            return (
                <Modal.Body>
                    <p>{"ダウンロードが完了しました"}</p>
                    <p>{"計測データ数: " + this.state.downloadedSamplingCount}</p>
                    <p>{"分析データ数: " + this.state.downloadedAnalysisCount}</p>
                </Modal.Body>
            );
        };

        return (
            <Modal
                show={this.state.showDownloadModal}
                onHide={() => {
                    this.setState({ showDownloadModal: false, downloadStatus: DownloadStatus.None})}
                }
                dialogClassName="modal-20w"
                aria-labelledby="example-custom-modal-styling-title"
            >
                <Modal.Header>
                    <Modal.Title id="example-custom-modal-styling-title">
                        {"ダウンロード"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {
                        this.state.downloadStatus === DownloadStatus.None ?  renderNoneBody() :
                        this.state.downloadStatus === DownloadStatus.Downloading ? renderDownloadingBody() :
                        this.state.downloadStatus === DownloadStatus.Completed ?  renderCompletedBody() : null
                    }
                    <a href="#" ref={ref => this.downloadTrigger = ref}></a>
                </Modal.Body>
                <Modal.Footer>
                    {
                        this.state.downloadStatus === DownloadStatus.Downloading ? null :
                            <Button variant="primary" type="button" onClick={this.onClickDownloadOKBound}>閉じる</Button>
                    }
                </Modal.Footer>
            </Modal>
        );
    }

    render() {
        return (
            <div>
                <NavBar {...this.props} />
                <div style={{margin: "50px"}}>
                    <h2 style={{textAlign: "center", marginBottom: "0px"}}>{this.getPageTitle()}</h2>
                    <div style={{display: "flex"}}>
                        <div>
                            <Button variant="primary" type="submit" onClick={this.onClickDownloadButtonBound}>
                                EXCELファイルでダウンロード
                            </Button>
                        </div>
                        <div style={{marginLeft: "auto"}}>
                            <Pagination>
                                <Pagination.Prev onClick={this.onClickPrevPageBound} />
                                <Pagination.Next onClick={this.onClickNextPageBound} />
                            </Pagination>
                        </div>
                    </div>
                    <SamplingResultsTable
                        samplingResults={this.state.samplingResults}
                        showDeleteFlag={this.getShowDeleteFlag()}
                        onRequestShowAnalysisResults={this.onRequestShowAnalysisResultsBound}
                        onImageLinkClicked={async (id, filename) => {
                            const url = await tryGetDownloadUrl(storageRef, createSamplingImageKey(id, filename));
console.log(url);
                            if (!url) {
                                alert("ファイルが見つかりません");
                                return;
                            }
                            window.open(url, "_blank");
                        }}
                    />
                </div>
                <Modal
                    show={this.state.showAnalysisModal}
                    onHide={() => this.setState({ showAnalysisModal: false})}
                    dialogClassName="modal-90w"
                    aria-labelledby="example-custom-modal-styling-title"
                >
                    <Modal.Header>
                        <Modal.Title id="example-custom-modal-styling-title">
                            {this.state.analysisResults.length
                            ? "分析データ一覧"
                            : "分析データはありません"
                            }
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <AnalysisResultsTable
                            analysisResults={this.state.analysisResults}
                            showDeleteFlag={this.getShowDeleteFlag()}
                            onImageLinkClicked={async (id, filename) => {
                                const url = await tryGetDownloadUrl(storageRef, createAnalysisImageKey(id, filename));
                                if (!url) {
                                    alert("ファイルが見つかりません");
                                    return;
                                }
                                window.open(url, "_blank");
                            }}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" type="button" onClick={this.onClickCloseModalBound}>閉じる</Button>
                    </Modal.Footer>
                </Modal>
                {this.renderDownloadModal()}
            </div>
        );
    }
}
